import { jwtDecode } from 'jwt-decode'

import { AuthHttp } from '../../http'
import type { UserSessionType } from '../../types/user.types'
import { isSuiteSsoRedirectMode } from '../sso-flags'
import { logInfo, logWarn } from '../utils/sso-logger'
import { clearSuiteAccessTokenCache, getSuiteAccessTokenFromIdentity } from './suite-access-token.service'

let cachedPayAccessToken: string | null = null
let cachedPayAccessTokenExpiresAt = 0

type PayAccessTokenResponse = {
  accessToken: string
  refreshToken?: string
}

function getStoredPayRefreshToken(): string {
  const raw = globalThis.sessionStorage.getItem('token') || globalThis.localStorage.getItem('token')
  return raw?.replaceAll('"', '') ?? ''
}

function storePayRefreshToken(refreshToken: string, activateAuth?: (token: { token: string }) => void): void {
  globalThis.sessionStorage.setItem('token', JSON.stringify(refreshToken))
  activateAuth?.({ token: refreshToken })
}

function cachePayAccessToken(accessToken: string): void {
  cachedPayAccessToken = accessToken

  try {
    const { exp } = jwtDecode<{ exp?: number }>(accessToken)
    cachedPayAccessTokenExpiresAt = exp ? exp * 1000 - 15_000 : Date.now() + 45_000
  } catch {
    cachedPayAccessTokenExpiresAt = Date.now() + 45_000
  }
}

function isCachedPayAccessTokenValid(now = Date.now()): boolean {
  return Boolean(cachedPayAccessToken && cachedPayAccessTokenExpiresAt > now)
}

async function requestPayAccessTokenFromAuth(bearerToken: string): Promise<PayAccessTokenResponse> {
  const response = await AuthHttp.post<PayAccessTokenResponse>(
    '/auth/request-access-token',
    {},
    {
      headers: {
        'Content-type': 'application/json',
        Authorization: `Bearer ${bearerToken}`
      }
    }
  )

  return response.data
}

/**
 * SSO bridge: Identity session → Pay /auth/request-access-token → RS256 access token
 * that app-data and other Pay microservices already trust.
 */
export async function resolvePayAccessTokenForMicroservices(
  activateAuth?: (token: { token: string }) => void
): Promise<string> {
  const now = Date.now()

  if (isCachedPayAccessTokenValid(now)) {
    return cachedPayAccessToken!
  }

  const payRefreshToken = getStoredPayRefreshToken()

  if (payRefreshToken) {
    try {
      logInfo('pay-auth-bridge — exchanging Pay refresh token for access token')
      const { accessToken, refreshToken } = await requestPayAccessTokenFromAuth(payRefreshToken)

      if (refreshToken) {
        storePayRefreshToken(refreshToken, activateAuth)
      }

      cachePayAccessToken(accessToken)
      return accessToken
    } catch (error) {
      logWarn('pay-auth-bridge — Pay refresh exchange failed, trying suite bridge', { error })
    }
  }

  if (!isSuiteSsoRedirectMode()) {
    throw new Error('Pay refresh token is required outside suite SSO mode')
  }

  logInfo('pay-auth-bridge — bridging suite Identity JWT to Pay access token')
  clearSuiteAccessTokenCache()

  const suiteToken = await getSuiteAccessTokenFromIdentity({ forceRefresh: true })
  const { accessToken, refreshToken } = await requestPayAccessTokenFromAuth(suiteToken)

  if (!refreshToken) {
    throw new Error('Pay refresh token missing from /auth/request-access-token response')
  }

  storePayRefreshToken(refreshToken, activateAuth)
  cachePayAccessToken(accessToken)

  logInfo('pay-auth-bridge — Pay session established from suite SSO', {
    hasAccessToken: Boolean(accessToken),
    hasRefreshToken: Boolean(refreshToken)
  })

  return accessToken
}

export function clearPayAccessTokenCache(): void {
  cachedPayAccessToken = null
  cachedPayAccessTokenExpiresAt = 0
}

type PayRefreshTokenPayload = {
  user_data?: UserSessionType
}

/**
 * Pay refresh JWT carries the Mongo system-user session in `user_data` (same as legacy login).
 */
export function resolvePayUserSessionFromRefreshToken(
  refreshToken = getStoredPayRefreshToken()
): UserSessionType | null {
  if (!refreshToken) {
    return null
  }

  try {
    const decoded = jwtDecode<PayRefreshTokenPayload>(refreshToken)
    const userData = decoded.user_data

    if (!userData?._id) {
      return null
    }

    return userData
  } catch (error) {
    logWarn('pay-auth-bridge — could not decode Pay refresh token user_data', { error })
    return null
  }
}
